import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { SelectItem } from "primeng/primeng";
import { Observable, Observer, Subscription } from "rxjs";
import * as lodash from "lodash";
import {
    ITCIAssetTag,
    ITCIMarketplaceListing,
    ITCIMarketplaceListingMetaData,
    ITCIMarketplaceV2MenuInput,
    ITCIMarketplaceV2MenuOutput,
    ITCIMarkupEditorContext,
    ITCIMediaAsset,
    ITCIUserInfo,
    TCI_ASSET_TAGS_COMPONENT_MODE,
    TCI_LISTING_UPSERT_MODE,
    TCI_MARKETPLACE_ACTIONS,
    TCI_MARKETPLACE_LISTING_EDITOR_MODE,
    TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    TCI_MARKETPLACE_ORG_TYPE,
    TCI_MARKETPLACE_PRICING_OPTIONS,
    TCI_MARKETPLACE_PUBLISH_TEXT,
    TCI_MARKUP_DISPLAY_MODE,
    TCI_MEDIA_EDIT_MODES,
    TCI_MEDIA_FIELD_NAME,
    TCIMarketplaceListingStatus,
    TCIMarkupEditorDisplaySize
} from "@tci/common";

import { UploadEvent } from "ngx-file-drop";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgMarketplaceV2Service } from "../ng-marketplace-v2.service";
import { ITCIUploadSupportedFile } from "../../ng-tenant/tci-tenant-components";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { NgMarketplaceV2ListingService } from "../ng-marketplace-v2-listing/ng-marketplace-v2-listing.service";
import { NgMediaService } from "../../ng-shared/ng-components/ng-media/ng-media.service";

@Component({
    selector: "ng-marketplace-v2-editor",
    templateUrl: "./ng-marketplace-editor.component.html",
    styleUrls: ["./ng-marketplace-v2-editor.component.less"]
})
export class NgMarketplaceV2EditorComponent implements OnInit, OnDestroy {
    /**
     * Input interface to use for Create/Edit Listing
     * The input may also come from the router navigate which should be
     * same as this structure for consistency.
     */
    @Input() input: ITCIMarketplaceV2MenuInput;
    @Input() userInfo: ITCIUserInfo;
    @Output() Close = new EventEmitter<ITCIMarketplaceV2MenuOutput<ITCIMarketplaceListing>>();

    @ViewChild("publishToMK") private publishToMK: NgForm;

    private subscriptions: Subscription[] = [];

    fileMedias: ITCIMediaAsset[] = [];

    isPreviewing = false;
    isShowUploadIconDialog = false;
    isShowUploadIconSuccessDialog = false;
    isShowCancelDialog = false;
    isShowWithdrawDialog = false;
    isDisableEULALink = false;
    isShowEULALink = false;
    isShowAdditionalLink = false;
    markupEditorMode: TCI_MARKUP_DISPLAY_MODE;
    editorSize: TCIMarkupEditorDisplaySize = TCIMarkupEditorDisplaySize.MEDIUM;
    publishBtnText: TCI_MARKETPLACE_PUBLISH_TEXT = TCI_MARKETPLACE_PUBLISH_TEXT.PUBLISH_PRIVATELY;
    isPublishBtnTextSet = false;
    isPublicListing = false;

    /**
     * Custom form fields
     * 1. Toggle isRequired property to make any custom field non-mandatory
     * 2. Keeping isEmpty property false in the beginning to hide error msg on initial
     *    form load.
     */
    isDescRequired = false;
    isDescEmpty = false;
    isPriceDetialRequired = false;
    isPriceDetialEmpty = false;
    isEULALinkRequired = false;
    isIconRequired = false;
    isIconEmpty = false;
    isMediaRequired = false;
    isMediaEmpty = false;
    fieldLimit = {
        priceDetail: {
            max: 80
        },
        displayName: {
            min: 3,
            max: 50
        }
    };

    inputDescriptionEditor: ITCIMarkupEditorContext = {
        content: "",
        encoding: ""
    };
    inputPriceDetailEditor: ITCIMarkupEditorContext;
    descTextareaId = "description-edtior";
    priceList: SelectItem[];
    listingData: ITCIMarketplaceListing;
    listingDataClone: any;
    listingDataGold: ITCIMarketplaceListing; // holds original listing data to evaluate isFormDirty
    isIconDirty = false;
    isMediaDirty = false;
    isFormValid = true;
    isPublishing = false;
    errorMsg = "";

    allowedCustomIconNumber = 1;
    allowedCustomIconSize = 1; // 1MB
    allowedCustomIcons: ITCIUploadSupportedFile[] = [];
    customIconErrorMsg: string;
    previewIconURL: string;
    previewIconName: string;
    isCustomIconDragOn = false;
    clickedCustomIconArea = false;

    // Asset tags
    tagsMode: TCI_ASSET_TAGS_COMPONENT_MODE = TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL;
    private tagsIsDirty = false;
    private newSelectedTags: ITCIAssetTag[] = [];
    private pristineSelectedTags: ITCIAssetTag[] = [];
    hasTagsList = false;
    allAssetTags: ITCIAssetTag[] = [];
    selectedTags: ITCIAssetTag[] = [];
    tagsPillVisible = false;
    tagsUpdateIsBusy = false;
    defaultFreePriceOption = TCI_MARKETPLACE_PRICING_OPTIONS.Free;

    // ng-media
    mediaMode = TCI_MEDIA_EDIT_MODES.EDIT_CREATE;
    mediaAssets: ITCIMediaAsset[] = [];
    isUpgrade = false;

    constructor(private userService: NgUserService,
        private mkService: NgMarketplaceV2Service,
        private mktListingSvc: NgMarketplaceV2ListingService,
        private toastr: NgToastrService,
        private mediaSvc: NgMediaService) {
    }

    ngOnInit(): void {
        console.log("The input data received in create/edit MK dialog: ", this.input);
        this.isShowUploadIconDialog = false;
        this.isShowUploadIconSuccessDialog = false;
        this.isUpgrade = TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE.toString() === this.input.editorMode;
        switch (this.input.editorMode) {
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE.toString():
                this.listingData = lodash.merge({
                    refData: {},
                    displayName: "",
                    iconUrl: "",
                    description: "",
                    mediaAssets: [],
                    infoUrl: "",
                    eulaUrl: "",
                    requestApproved: false,
                    requestUrl: "",
                    dirty: false,
                    tags: [],
                    managingOrgId: "",
                    managingOrgName: "",
                    listingStatus: {
                        [this.userInfo.gsbc]: [TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE]
                    },
                    listingOrgMap: {
                        [TCI_MARKETPLACE_ORG_TYPE.Provider.toString()]: [this.userInfo.gsbc],
                        [TCI_MARKETPLACE_ORG_TYPE.Consumer.toString()]: [this.userInfo.gsbc],
                    },
                    listingOrgTypeStatusMap: {
                        [TCI_MARKETPLACE_ORG_TYPE.Provider.toString()]:
                            TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE,
                        [TCI_MARKETPLACE_ORG_TYPE.Consumer.toString()]: undefined,
                    },
                    pricingInfo: {
                        pricingOptions: this.defaultFreePriceOption,
                        details: ""
                    },
                    nextAllowedActions: [],
                    eulaAccepted: false,
                    subscribed: false,
                    hasUpgrade: false,
                    upgradeVersion: "",
                    numPendingAccessRequests: 0,
                    purchasedByUserOrg: false,
                } as ITCIMarketplaceListing, this.input.listing as ITCIMarketplaceListingMetaData);
                this.setPublishBtnText(this.listingData, TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE, this.userInfo);
                this.listingDataGold = lodash.cloneDeep(this.listingData);
                this.initCommons(this.listingData);
                break;
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE.toString():
                this.subscriptions.push(this.preFillListingDataByAssetId(this.input.listing.creatorAssetId,
                    TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE, this.userInfo)
                    .subscribe(
                        preFillRes => {
                            this.initCommons(preFillRes);
                            this.upgradeListingData(preFillRes, this.userInfo);
                        },
                        err => this.toastr.error(err)
                    ));
                break;
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT.toString():
                this.subscriptions.push(this.preFillListingDataByAssetId(this.input.listing.creatorAssetId,
                    TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT, this.userInfo)
                    .subscribe(
                        preFillRes => this.initCommons(preFillRes),
                        err => this.toastr.error(err)
                    ));
                break;
        }
        this.markupEditorMode = TCI_MARKUP_DISPLAY_MODE.MODE_EDIT;
        this.initFilesInput();
    }

    initCommons(prefillListingRes: ITCIMarketplaceListing) {
        this.listingDataClone = lodash.cloneDeep(prefillListingRes);

        this.inputDescriptionEditor = {
            content: prefillListingRes && prefillListingRes.description ? prefillListingRes.description : "",
            encoding: ""
        };
        this.inputPriceDetailEditor = {
            content: prefillListingRes && prefillListingRes.pricingInfo ? prefillListingRes.pricingInfo.details : "",
            encoding: ""
        };
        if (!prefillListingRes.pricingInfo) {
            prefillListingRes.pricingInfo = {
                pricingOptions: this.defaultFreePriceOption,
                details: ""
            };
        }
        this.priceList = [{
            label: TCI_MARKETPLACE_PRICING_OPTIONS.Free,
            value: TCI_MARKETPLACE_PRICING_OPTIONS.Free
        }, {
            label: TCI_MARKETPLACE_PRICING_OPTIONS.Paid,
            value: TCI_MARKETPLACE_PRICING_OPTIONS.Paid
        }];
        this.initTags();
        this.initFieldFromPrice(prefillListingRes.pricingInfo.pricingOptions, prefillListingRes.requestUrl);
    }

    ngOnDestroy(): void {
        lodash.forEach(
            lodash.values(this.subscriptions),
            (subscription: Subscription) => subscription && subscription.unsubscribe()
        );
    }

    // Placeholder to check for clicks inside the component
    // Can be used to auto close the other dialogs, if the dialog should be closed on clicking outside of it.
    @HostListener("click", ["$event"])
    clickout(e) {
        if (this.isShowUploadIconDialog && !this.clickedCustomIconArea) {
            this.isShowUploadIconDialog = false;
        }
        this.clickedCustomIconArea = false;
    }

    private preFillListingDataByAssetId(
        creatorAssetId: string,
        editMode: TCI_MARKETPLACE_LISTING_EDITOR_MODE,
        userInfo: ITCIUserInfo
    ): Observable<ITCIMarketplaceListing> {
        return Observable.create((obs: Observer<any>) => {
            this.subscriptions.push(this.mkService.getListingByAssetId(creatorAssetId)
                .catch(err => Observable.throw(err))
                .subscribe(res => {
                    if (res.data && res.data[0]) {
                        this.listingData = res.data[0] as ITCIMarketplaceListing;

                        // IPAS-17152, only override version and name form meta data when mode is upgrade
                        if (TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE === editMode) {
                            this.listingData = Object.assign(this.listingData, {
                                "version": this.input.listing.version,
                                "name": this.input.listing.name,
                            });
                        }
                        const orgStatusMap = {};
                        Object.entries(res.data[0].listingOrgTypeStatusMap)
                            .forEach(([orgType, status]) => {
                                orgStatusMap[orgType] =
                                    status ? TCIMarketplaceListingStatus.fromJSON(status) : null;
                            });
                        this.listingData.listingOrgTypeStatusMap = orgStatusMap;
                        this.listingDataGold = lodash.cloneDeep(this.listingData);
                        this.mediaMode = TCI_MEDIA_EDIT_MODES.EDIT_UPDATE;
                        this.setPublishBtnText(this.listingData, editMode, userInfo);
                        this.initIconFromUrl(this.listingData);
                        this.afterPrefillElementState();

                        obs.next(this.listingData);
                        obs.complete();
                    } else {
                        obs.error(new Error(`Failed to load listing by ${creatorAssetId}`));
                        obs.complete();
                    }
                }, err => {
                    obs.error(err);
                    obs.complete();
                }));
        });
    }

    /**
     upgrade listing data
     1. listingData is from getListingDataByAssetId API response.
     2. if it is public, will change it to private at first.
     3. call API this.mkService.upsertListing() to save data with new upgrade version
     */
    // tslint:disable-next-line:max-line-length
    // https://confluence.tibco.com/pages/viewpage.action?spaceKey=IPAAS&title=TCI+Marketplace+UI+V2+architecture#TCIMarketplaceUIV2architecture-Add/EditMarketplaceDialog
    private upgradeListingData(listingData: ITCIMarketplaceListing, userInfo: ITCIUserInfo) {
        const listingStatus: TCIMarketplaceListingStatus[] = listingData.listingStatus &&
            listingData.listingStatus[userInfo.gsbc] || [];
        const status = listingStatus.map(s => TCIMarketplaceListingStatus.fromJSON(s));
        let obser = Observable.of(listingData);
        if (status.includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE)) {
            // change it to private at first
            obser = Observable.create((obs: Observer<any>) => {
                this.subscriptions.push(this.mkService.processAction({
                    listing: listingData,
                    actionType: TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPrivate
                })
                    .catch(err => Observable.throw(err))
                    .subscribe(res => {
                        if (res.data && res.data[0]) {
                            obs.next(res.data[0]);
                            obs.complete();
                        } else {
                            obs.error(new Error(`Update visibility response data can not be null.`));
                            obs.complete();
                        }
                    }, err => {
                        obs.error(err);
                        obs.complete();
                    }));
            });
        }
        this.subscriptions.push(
            obser.subscribe((visibilityListingData: ITCIMarketplaceListing) => {
                // Note: visibilityListingData is the latest data from server
                // this.input.listing is the data from tenant
                // use the tenant new version and name to override visibilityListingData, then save it into database.
                visibilityListingData = Object.assign(visibilityListingData, {
                    "version": this.input.listing.version,
                    "name": this.input.listing.name,
                });
                this.subscriptions.push(
                    this.mkService.upsertListing(
                        visibilityListingData,
                        this.getUpsertMode(),
                        this.previewIconURL,
                        this.fileMedias).subscribe(upsertRes => {
                            if (upsertRes.success) {
                                const newListingData: ITCIMarketplaceListing =
                                    Array.isArray(upsertRes.data) ? upsertRes.data[0] : {} as ITCIMarketplaceListing;

                                this.toastr.success(`Marketplace listing '${newListingData.displayName}' is now upgraded.`);
                                this.mkService.notifyChange({
                                    type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Update,
                                    listing: newListingData
                                });
                                this.closeMKDialog(newListingData, TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.UPGRADE, false);
                            }
                        })
                );
            })
        );
    }

    setPublishBtnText(listingData: ITCIMarketplaceListing, mode: string, userInfo: ITCIUserInfo): void {
        let text: TCI_MARKETPLACE_PUBLISH_TEXT;
        const listingStatus: TCIMarketplaceListingStatus[] = listingData.listingStatus &&
            listingData.listingStatus[userInfo.gsbc] || [];
        const status = listingStatus.map(s => TCIMarketplaceListingStatus.fromJSON(s));
        switch (mode) {
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT:
                if (status.includes(TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE)) {
                    text = TCI_MARKETPLACE_PUBLISH_TEXT.UPDATE_LISTING;
                } else if (status.includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE)) {
                    // Note: if isHostOrgListing is true, that means current user belongs to TIBCO org
                    // change PUBLIC listing do not need to be approved again
                    // UI side will show "Update listing" button, backend will approve it directly.
                    if (this.listingData.isHostOrgListing) {
                        text = TCI_MARKETPLACE_PUBLISH_TEXT.UPDATE_LISTING;
                    } else {
                        text = TCI_MARKETPLACE_PUBLISH_TEXT.SUBMIT_APPROVAL;
                    }
                    this.isPublicListing = true;
                } else {
                    // Keeping this as default text in edit dialog if status match fails.
                    text = TCI_MARKETPLACE_PUBLISH_TEXT.UPDATE_LISTING;
                }
                break;
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE:
                text = TCI_MARKETPLACE_PUBLISH_TEXT.UPGRADE_LISTING;
                break;
            default:
                // for create listing
                text = TCI_MARKETPLACE_PUBLISH_TEXT.PUBLISH_PRIVATELY;
        }
        this.publishBtnText = text;
        this.isPublishBtnTextSet = true;
    }

    initIconFromUrl(listingData: ITCIMarketplaceListing) {
        if (listingData.iconUrl) {
            this.subscriptions.push(this.mediaSvc.getBase64FromUrl(listingData.iconUrl)
                .subscribe(dataUrl => {
                    listingData.iconContent = this.previewIconURL = dataUrl;
                }));
        }
    }

    afterPrefillElementState() {
        if (this.listingData.mediaAssets.length) {
            this.mediaAssets = this.listingData.mediaAssets;
        }
        this.isShowEULALink = this.listingData.eulaUrl !== "";
        this.isShowAdditionalLink = this.listingData.infoUrl !== "";
    }

    toggleCancelConfirmationDialog(bool: boolean) {
        /**
         * Omitting below props from comparision as they are always updated initially in edit mode (media path to dataURl).
         * Instead using isIconDirty & isMediaDirty for them.
         */
        const ignoreProps = ["iconContent", "mediaAssets"];
        const goldSet = lodash.omit(this.listingDataGold, ignoreProps);
        const currentSet = lodash.omit(this.listingData, ignoreProps);

        // If no changes made to form by user, then dont show cancel confirmation.
        if (lodash.isEqual(goldSet, currentSet) && !this.isIconDirty && !this.isMediaDirty) {
            this.closeMKDialog();
        } else {
            this.isShowCancelDialog = bool;
        }
    }

    toggleWithdrawConfirmationDialog(bool: boolean) {
        this.isShowWithdrawDialog = bool;
    }

    // for add custom icon ---------
    initFilesInput() {
        this.allowedCustomIcons = [
            {
                extension: "jpg",
                finalFileName: "mk-custom-icon.jpg"
            },
            {
                extension: "jpeg",
                finalFileName: "mk-custom-icon.jpeg"
            },
            {
                extension: "png",
                finalFileName: "mk-custom-icon.png"
            }
        ];
    }

    dropped(event: UploadEvent) {
        const files: File[] = [];
        let processed = 0;
        event.files
            .filter(f => f.fileEntry.isFile)
            .forEach(f => {
                f.fileEntry.file((fe: File) => {
                    files.push(fe);

                    processed++;
                    if (processed === event.files.length) {
                        this.handleSelectedFiles(files);
                    }
                });
            });
    }

    private customIconErrorMsgHide() {
        this.customIconErrorMsg = "";
    }

    private handleSelectedFiles(files: File[]) {
        if (files.length === 0) {
            return;
        }

        this.customIconErrorMsgHide();
        const emitErr = (msg: string) => {
            this.customIconErrorMsg = msg;
        };
        if (this.validateIconSize(files[0])) {
            emitErr(`There are files larger than ${this.allowedCustomIconSize}MB in your selection.`);
        } else if (files.length === this.allowedCustomIconNumber) {
            if (lodash.map(this.allowedCustomIcons, "extension").includes(files[0].name.split(".").pop().toLowerCase())) {
                this.previewIcon(files[0]);
                this.isIconDirty = true;
            } else {
                emitErr("The file you’re trying to upload is not a .PNG or .JPG format.");
            }
        } else {
            emitErr("There are more selected files than expected");
        }
    }

    /**
     * Current limit of icon file size is 1 MB
     */
    private validateIconSize(files: File): boolean {
        let sizeFlag = false;
        const s = parseFloat((files.size / (1024 * 1024)).toFixed(2)); // allows upto 0.9 MB
        if (s > this.allowedCustomIconSize) {
            sizeFlag = true;
        }
        return sizeFlag;
    }

    previewIconFile(e) {
        this.handleSelectedFiles(<File[]>lodash.toArray(e.target.files));
    }

    private previewIcon(file: File) {
        const reader = new FileReader();
        reader.onload = () => {
            this.previewIconURL = <string>reader.result;
            this.previewIconName = file.name;
            if (this.previewIconURL) {
                this.listingData.iconContent = this.previewIconURL;
                this.isIconEmpty = false;
                this.toggleUploadIcon(false);
            }
        };
        reader.readAsDataURL(file);
    }

    toggleUploadIcon(isShow: boolean) {
        this.clickedCustomIconArea = true;
        if (isShow) {
            this.isShowUploadIconSuccessDialog = (this.previewIconURL && this.previewIconURL !== "");
            if (this.isShowUploadIconSuccessDialog) {
                this.previewIconName = this.previewIconName || this.listingData.iconUrl.split("/").pop();
            }
        }
        this.isShowUploadIconDialog = isShow;
        this.resetCustomIconDrag();
    }

    hideUploadIconSuccessDialog() {
        this.isShowUploadIconSuccessDialog = false;
        this.toggleUploadIcon(false);
    }

    removeCustomIcon() {
        this.previewIconURL = "";
        this.previewIconName = "";
        this.listingData.iconContent = "";
        this.listingData.iconUrl = "";
        this.isIconEmpty = false;
        this.isIconDirty = (!this.listingDataGold.iconUrl) ? false : true;
        this.toggleUploadIcon(false);
    }

    toggleCustomIconDrag(value) {
        this.isCustomIconDragOn = value;
    }

    ignoreDrag(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    CustomIconAreaClicked(e) {
        this.clickedCustomIconArea = true;
    }

    // reseting the drag over styles.
    resetCustomIconDrag() {
        this.isCustomIconDragOn = false;
        this.customIconErrorMsg = "";
    }

    // for add custom icon ---------

    resetDescription() {
        // Angular markdown editor is not detecting the change on setting the formControl value binding to '',
        // So I had to change to ' ' and trim the value while validating the Publish Marketplace form.
        this.listingData.description = this.listingDataClone.description ? this.listingDataClone.description : " ";
        // For child components to listen for inputDescriptionEditor changes
        this.inputDescriptionEditor = { ...this.inputDescriptionEditor, content: this.listingData.description };
    }

    editorContextChange(context: ITCIMarkupEditorContext) {
        this.listingData.description = context.content;
        this.inputDescriptionEditor.content = context.content;
        this.isDescEmpty = !context.content;
    }

    /**
     * 1. by default price is Free, request URL is blank, EULA link is disabled, no value.
     * 2. typed a valid request URL, EULA link should be enabled, and become required field.
     * 3. if user remove the request URL, EULA link should be disabled, and clear the value.
     * 4. change price to Paid, request URL is not required, EULA link should be enabled, and required field.
     * @param price
     * @param requestUrl
     */
    initFieldFromPrice(price: TCI_MARKETPLACE_PRICING_OPTIONS, requestUrl: string) {
        if (price === TCI_MARKETPLACE_PRICING_OPTIONS.Free && requestUrl === "") {
            this.isDisableEULALink = true;
        }
        if (price === TCI_MARKETPLACE_PRICING_OPTIONS.Paid || this.isUrlValid(requestUrl)) {
            this.isEULALinkRequired = true;
        }
    }
    changePriceOrRequestUrl(price: TCI_MARKETPLACE_PRICING_OPTIONS, requestUrl: string) {
        if (price === TCI_MARKETPLACE_PRICING_OPTIONS.Free) {
            if (this.isUrlValid(requestUrl)) {
                this.isDisableEULALink = false;
                this.isEULALinkRequired = true;
            } else {
                this.isDisableEULALink = true;
                this.isEULALinkRequired = false;
                this.clearEULAlLink();
            }
            this.listingData.pricingInfo.details = "";
        }
        if (price === TCI_MARKETPLACE_PRICING_OPTIONS.Paid) {
            this.isDisableEULALink = false;
            this.isEULALinkRequired = true;
        }
    }

    clearEULAlLink() {
        this.listingData.eulaUrl = "";
    }

    toggleEULAlLink() {
        this.isShowEULALink = !this.isShowEULALink;
    }

    clearAdditionalLink() {
        this.listingData.infoUrl = "";
    }

    toggleAdditionalLink() {
        this.isShowAdditionalLink = !this.isShowAdditionalLink;
    }

    validateMarketplaceListingData(data: ITCIMarketplaceListing): void {
        /**
         * Custom form elements (like description, custom icon, media),
         * need to be explictly checked whereas
         * native form elements are taken care by angular.
         */
        /**
         * Custom form fields
         */
        this.isFormValid = true;
        this.errorMsgHide();

        /* if (!data.iconUrl) {
            this.isFormValid = false;
            this.isIconEmpty = true;
        }
        if (!data.description) {
            this.isFormValid = false;
            this.isDescEmpty = true;
        }
        if (!data.pricingInfo.details) {
            this.isFormValid = false;
            this.isPriceDetialEmpty = true;
        }
        if (!data.mediaAssets || (Array.isArray(data.mediaAssets) && data.mediaAssets.length === 0)) {
            this.isFormValid = false;
            this.isMediaEmpty = true;
        } */
        /**
         * Angular form fields.
         * Manually marking native required form elements as touched to show
         * validation error in UI if user skips them and direclty clicks publish button.
         */
        if (data.displayName) {
            this.publishToMK.controls["mk_display_name"].markAsTouched();
            if (data.displayName.length < this.fieldLimit.displayName.min) {
                this.isFormValid = false;
                this.publishToMK.controls["mk_display_name"].setErrors({ "minLength": true });
            }
            if (data.displayName.length > this.fieldLimit.displayName.max) {
                this.isFormValid = false;
                this.publishToMK.controls["mk_display_name"].setErrors({ "maxLength": true });
            }
        } else {
            this.publishToMK.controls["mk_display_name"].markAsTouched();
        }
        if (data.pricingInfo && data.pricingInfo.details && data.pricingInfo.details.length > this.fieldLimit.priceDetail.max) {
            this.isFormValid = false;
            this.publishToMK.controls["mk_price_info-detail"].markAsTouched();
            this.publishToMK.controls["mk_price_info-detail"].setErrors({ "maxLength": true });
        }
        if (data.requestUrl && !this.isUrlValid(data.requestUrl)) {
            this.isFormValid = false;
            this.publishToMK.controls["mk_request_url"].markAsTouched();
            this.publishToMK.controls["mk_request_url"].setErrors({ "url": true });
        }
        if (this.isEULALinkRequired && !this.isUrlValid(data.eulaUrl)) {
            this.isFormValid = false;
            this.publishToMK.controls["mk_eula_url"].markAsTouched();
            this.publishToMK.controls["mk_eula_url"].setErrors({ "url": true });
        }
        /*
        if (!data.infoUrl) {
            this.publishToMK.controls["mk_info_url"].markAsTouched();
        } */
    }

    handleUrl(control: string) {
        const fc = this.publishToMK.controls[control];
        const value = fc.value;

        if (value && !this.isUrlValid(value)) {
            fc.setErrors({ "url": true });
        } else {
            fc.setErrors({ "url": null });
            fc.updateValueAndValidity();
        }
        if (control === "mk_request_url") {
            this.changePriceOrRequestUrl(this.listingData.pricingInfo.pricingOptions, value);
        }
    }

    handleInputLength(control: string) {
        const fc = this.publishToMK.controls[control];
        const value = fc.value.trim();

        if (value) {
            const data = this.trimListingData(lodash.cloneDeep(this.listingData));
            this.validateMarketplaceListingData(data);
        }
    }

    isUrlValid(url: string): boolean {
        const res = url.match(/(http(s)?):\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
        return res !== null;
    }

    public errorMsgHide() {
        this.errorMsg = undefined;
    }

    getDialogTitle(): string {
        let title = "Create marketplace listing";
        if (this.isPreviewing) {
            title = "Preview marketplace listing";
        } else if (this.input.editorMode === TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT) {
            title = "Edit marketplace listing";
        } else if (this.input.editorMode === TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE) {
            title = "Upgrade marketplace listing";
        }
        return title;
    }

    preview() {
        this.isPreviewing = true;
        this.isShowCancelDialog = false;
    }

    cancelPreview() {
        this.isPreviewing = false;
        this.isShowWithdrawDialog = false;
        this.isShowCancelDialog = false;
    }

    closeMKDialog(newListing?: ITCIMarketplaceListing,
        submitActionType?: TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION,
        cancel: boolean = true) {
        const output = {
            /**
             * Modal is closing
             */
            closeModal: true,
            /**
             * type of submit action
             */
            submitActionType: submitActionType || TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.CANCEL,
            /**
             * modal is cancelled
             */
            cancel: cancel,
            /**
             * refresh parent
             */
            refresh: true,
            /**
             * context data during cancel/submit
             */
            result: newListing || this.input.listing
        } as ITCIMarketplaceV2MenuOutput<ITCIMarketplaceListing>;
        this.Close.emit(output);
    }

    getUpsertMode(): TCI_LISTING_UPSERT_MODE {
        switch (this.input.editorMode) {
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.CREATE.toString():
                return TCI_LISTING_UPSERT_MODE.create;
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.UPGRADE.toString():
                return TCI_LISTING_UPSERT_MODE.upgrade;
            case TCI_MARKETPLACE_LISTING_EDITOR_MODE.EDIT.toString():
                return TCI_LISTING_UPSERT_MODE.update;
            default:
                return TCI_LISTING_UPSERT_MODE.create;
        }
    }

    trimListingData(data: ITCIMarketplaceListing): ITCIMarketplaceListing {
        data.displayName = data.displayName.trim();
        data.pricingInfo.details = data.pricingInfo.details.trim();
        data.description = data.description.trim();
        return data;
    }

    publishMK() {
        // IPAS-18867
        this.saveSelectedTags();
        const data: ITCIMarketplaceListing = this.trimListingData(lodash.cloneDeep(this.listingData));
        console.log("get marketplace data and post to API: ", data);
        this.validateMarketplaceListingData(data);
        if (this.isFormValid && this.publishToMK.valid) {
            this.isPublishing = true;
            /**
             * Choose upsert mode
             * @param editorMode
             */
            this.mkService.upsertListing(data,
                this.getUpsertMode(),
                this.previewIconURL,
                this.fileMedias)
                .finally(() => this.isPublishing = false)
                .subscribe(upsertRes => {
                    if (upsertRes.success) {
                        let changeType: TCI_MARKETPLACE_NOTIFICATION_TYPE;
                        let submitActionType: TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION;
                        if (this.listingData.id === "") {
                            this.toastr.clear();
                            this.toastr.success(
                                `Your Marketplace item has been posted and is now visible to members of your organization.`,
                                `You can update visibility to Public later.`).setLife(5000);
                            changeType = TCI_MARKETPLACE_NOTIFICATION_TYPE.Create;
                            submitActionType = TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.SUBMIT_PRIVATE_NEW;
                        } else {
                            if (!this.isPublicListing) {
                                this.toastr.success(
                                    `Your Marketplace item has been updated and is now visible to members of your organization.`
                                ).setLife(5000);
                            }
                            submitActionType = TCI_MARKETPLACE_LISTING_EDITOR_SUBMIT_ACTION.SUBMIT_PRIVATE_UPDATE;
                            changeType = TCI_MARKETPLACE_NOTIFICATION_TYPE.Update;
                        }
                        // If isPublicListing, then trigger submit for approval after update listing
                        if (this.isPublicListing) {
                            // this.mkService.submitApproval(upsertRes.data[0].id).subscribe(submitApprovalRes => {
                            this.mkService.processAction(
                                {
                                    listing: upsertRes.data[0],
                                    actionType: TCI_MARKETPLACE_ACTIONS.SubmitForApproval
                                })
                                .subscribe(submitApprovalRes => {
                                    if (submitApprovalRes.success) {
                                        // close dialog and notify with submitApproval res
                                        // Emit change notification
                                        if (this.listingData.isHostOrgListing || this.listingData.enabledByDefault) {
                                            this.toastr.success(
                                                `Your Marketplace item has been updated and is now visible to members of your organization.`
                                            ).setLife(5000);
                                        } else {
                                            this.toastr.success(
                                                `Your Marketplace item has been updated and is now pending approval.`,
                                                `The item is currently visible only to members of your organization.`
                                            ).setLife(5000);
                                        }
                                        this.mkService.notifyChange({
                                            type: changeType,
                                            listing: submitApprovalRes.data[0]
                                        });
                                        this.closeMKDialog(submitApprovalRes.data[0], submitActionType, false);
                                    } else {
                                        this.toastr.error(submitApprovalRes.error.message);
                                    }
                                }, err => {
                                    this.handleError(err);
                                });
                        } else {
                            // create new listing and private listing update has no after upsert hook
                            // close dialog and notify with upsertRes
                            this.mkService.notifyChange({ type: changeType, listing: upsertRes.data[0] }); // Emit change notification
                            this.closeMKDialog(upsertRes.data[0], submitActionType, false);
                        }
                    } else {
                        this.toastr.error(upsertRes.error.message);
                    }
                }, err => {
                    this.handleError(err);
                });
        } else {
            this.errorMsg = "There was a problem with your submission. See highlighted messages.";
        }
    }

    handleError(err: any) {
        let errorMsg = "Something went wrong.";
        try {
            errorMsg = JSON.parse(err._body).msg;
        } catch (e) {
            console.error("Unable to parse error body ", e);
        }
        this.toastr.error(errorMsg);
    }

    onBeforeShowMarkup(event) {

    }

    onAfterHideMarkup(event) {

    }

    onBeforeShowMedia(assets: ITCIMediaAsset[]) {
        this.prepareMedia(assets);
    }

    onAfterHideMedia(assets: ITCIMediaAsset[]) {
        this.prepareMedia(assets);
        this.isMediaDirty = (!this.listingDataGold.mediaAssets.length && !assets.length) ? false : true;
    }

    prepareMedia(assets: ITCIMediaAsset[]) {
        assets.forEach((asset, i) => {
            asset.fieldName = TCI_MEDIA_FIELD_NAME.MEDIA_ICON + (i + 1);
        });
        this.fileMedias = assets;
        if (this.listingData) {
            this.listingData.mediaAssets = assets;
            this.isMediaEmpty = Array.isArray(this.listingData.mediaAssets) ?
                this.listingData.mediaAssets.length === 0 : true;
        }
    }

    private initTags() {
        this.subscriptions.push(
            this.mkService.getMarketplaceTags()
                .map((res: string[]) => {
                    const tags: ITCIAssetTag[] = [];
                    res.forEach(tag => {
                        tags.push({
                            "name": tag
                        });
                    });
                    return tags;
                })
                .subscribe((tags: ITCIAssetTag[]) => {
                    this.allAssetTags = tags;
                    this.populateTagsData();
                    this.hasTagsList = true;
                    this.tagsPillVisible = true;
                })
        );
    }

    private populateTagsData() {
        this.selectedTags = this.listingData && lodash.cloneDeep(this.listingData.tags) || [];
        this.pristineSelectedTags = this.listingData && lodash.cloneDeep(this.listingData.tags) || [];
    }

    saveSelectedTags(): void {
        if (this.tagsIsDirty) {
            this.listingData.tags = this.newSelectedTags;
            this.populateTagsData();
        }
    }

    storeSelectedTags(selectedTags: ITCIAssetTag[]) {
        if (this.hasTagsList) { // Ensure that the tags are bound to the app
            this.newSelectedTags = lodash.cloneDeep(selectedTags);

            const leftPart = this.pristineSelectedTags.map(t => t.name).sort().join();
            const rightPart = this.newSelectedTags.map(t => t.name).sort().join();
            this.tagsIsDirty = leftPart !== rightPart;
        } else {
            console.error("The tags are not bound to the app yet");
        }
    }
}
